import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import {
  AutocompleteSearch,
  LoadingSkeleton,
  Switch,
  showNotification,
  Tag,
  useQueryFetch
} from 'sonata-react-components';
import { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';

import { assignDealToUsers, getDealAssignedUsers, unassignDeal } from '@/services/assignDeals.service';
import { getSearchUsers } from '@/services/masquerade.service';

import './assignDealSection.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

export type AssignedUser = Pick<UserInterface, 'id' | 'name'>;

type SuggestionUser = {
  email: UserInterface['email'];
  id: string;
  label: string;
  name?: UserInterface['name'];
  value: UserInterface;
};

type BaseProps = {
  className?: string;
  onSuccess?: () => void;
  user: UserInterface;
};

type Props =
  | (BaseProps & {
      assignedUsers?: never;
      deal: Partial<DealInterface<string>>;
      onSetAssignedUsers?: never;
    })
  | (BaseProps & {
      assignedUsers: AssignedUser[];
      deal?: never;
      onSetAssignedUsers: (users: AssignedUser[]) => void;
    });

export function AssignDealSection({
  assignedUsers: propAssignedUsers,
  className,
  deal,
  onSetAssignedUsers,
  onSuccess,
  user
}: Props) {
  const { formatMessage } = useIntl();

  const token = {};

  const {
    data: fetchedAssignedUsers,
    isFetching: isFetchingAssignedUsers,
    refetch: refetchAssignedUsers
  } = useQueryFetch(['fetchAssignedUsers', deal?._id], () => getDealAssignedUsers(deal!._id!, token), {
    enabled: !!deal,
    placeholderData: []
  });

  const assignedUsers = deal ? fetchedAssignedUsers : propAssignedUsers;

  const assignUsersMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      return assignDealToUsers(deal!._id!, userIds);
    },
    onError: () => {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Something went wrong',
          id: 'menu.assignDealsModal.toast.error'
        })
      );
    },
    onSuccess: () => {
      refetchAssignedUsers();
      onSuccess?.();
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Deals assigned successfully',
          id: 'menu.assignDealsModal.toast.success'
        })
      );
    }
  });

  const fetchData = async (searchText: string): Promise<SuggestionUser[]> => {
    const users = await getSearchUsers(searchText, {});

    return users.map(user => {
      return {
        email: user.email,
        id: user.id || (user._id as string),
        label: user.name || user.email,
        name: user.name,
        value: user
      };
    });
  };

  const handleAssignUsers = (userIds: string[]) => {
    assignUsersMutation.mutate(userIds);
  };

  const handleUserSelectionChange = async (suggestion: { value: UserInterface } | null) => {
    if (assignedUsers?.map(u => u.id).includes(suggestion?.value.id || '')) {
      return showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'The user is already assigned to this deal.',
          id: 'menu.assignDealsModal.toast.userAlreadyAssigned'
        })
      );
    }
    const newUserIds = [...(assignedUsers ?? []).map(u => u?.id), suggestion?.value?.id].filter(Boolean) as string[];
    if (deal) {
      return await handleAssignUsers(newUserIds);
    }
    onSetAssignedUsers?.(
      assignedUsers
        ? [...assignedUsers, { id: suggestion!.value.id, name: suggestion!.value.name || '' }]
        : [{ id: suggestion!.value.id, name: suggestion!.value.name || '' }]
    );
  };

  const unassignUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return unassignDeal(deal!._id!, [userId]);
    },
    onError: () => {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Something went wrong',
          id: 'menu.assignDealsModal.toast.error'
        })
      );
    },
    onSuccess: () => {
      refetchAssignedUsers();
      onSuccess?.();
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Deal unassigned successfully',
          id: 'menu.assignDealsModal.toast.unassignSuccess'
        })
      );
    }
  });

  const handleUnassignUser = (userId: string) => () => {
    if (deal) {
      return unassignUserMutation.mutate(userId);
    }
    onSetAssignedUsers?.(assignedUsers?.filter(({ id }) => id !== userId) || []);
  };

  function getSortedAssignedUsers(assignedUsers: AssignedUser[], user: UserInterface): AssignedUser[] {
    const selfUserIndex = assignedUsers.findIndex(({ id }) => id === user.id);
    let sortedAssignedUsers = structuredClone(assignedUsers);
    if (selfUserIndex !== -1) {
      sortedAssignedUsers = [...assignedUsers.slice(0, selfUserIndex), ...assignedUsers.slice(selfUserIndex + 1)];
    }
    sortedAssignedUsers.sort(({ name: name1 }, { name: name2 }) => {
      return name1.localeCompare(name2, undefined, {
        ignorePunctuation: true,
        sensitivity: 'base'
      });
    });
    if (selfUserIndex !== -1) {
      sortedAssignedUsers = [assignedUsers[selfUserIndex], ...sortedAssignedUsers];
    }
    return sortedAssignedUsers;
  }

  return (
    <div className={classNames('assignDealSection', className)}>
      <Switch>
        <Switch.Case condition={deal ? isFetchingAssignedUsers : false}>
          <LoadingSkeleton />
        </Switch.Case>
        <Switch.Default>
          <div className="assignDealSection__userSelectorTopContainer">
            <AutocompleteSearch<SuggestionUser>
              fetchData={fetchData}
              id="autocompleteSearchFetch"
              label={formatMessage({
                defaultMessage: 'Search user',
                id: 'changeUserModal.userSearchLabel'
              })}
              onSelectionChange={handleUserSelectionChange}
              placeholder={formatMessage({
                defaultMessage: 'Type the name, email...',
                id: 'changeUserModal.userSearchPlaceholder'
              })}
              showInputLens
            >
              {suggestions =>
                suggestions.map(suggestion => (
                  <AutocompleteSearch.Suggestion id={suggestion.id} key={suggestion.id}>
                    <div className="assignDealSection__suggestionContainer">
                      <span className="assignDealSection__suggestionName">{suggestion.name}</span>
                      <span className="assignDealSection__suggestionEmail">{suggestion.email}</span>
                    </div>
                  </AutocompleteSearch.Suggestion>
                ))
              }
            </AutocompleteSearch>
          </div>
          {assignedUsers && assignedUsers?.length > 0 && (
            <div className="assignDealSection__userSelectorBottomContainer">
              {getSortedAssignedUsers(assignedUsers, user).map(({ id: userId, name }) => (
                <Tag
                  icon={{
                    position: 'right',
                    webFont: 'fa-solid fa-xmark'
                  }}
                  id={`userAssignedDeal-${userId}`}
                  key={`userAssignedDeal-${userId}`}
                  onClick={handleUnassignUser(userId!)}
                >
                  {name}
                </Tag>
              ))}
            </div>
          )}
        </Switch.Default>
      </Switch>
    </div>
  );
}
